/**
 * Single-slot serializer for passive Word read probes.
 *
 * The addin fires several independent Word.run() reads on the same UI
 * events — empty-doc check + cursor-section detection on selection
 * settle, plus a state-transition empty-doc check. Office.js will
 * queue them internally, but firing them concurrently from React lets
 * the queue grow unbounded under rapid input, and makes failure modes
 * (one Word.run rejecting while another races behind it) hard to
 * reason about. Routing the reads through one promise chain keeps
 * them strictly sequential without needing a third-party limiter.
 *
 * Intentionally NOT used for draft writes / section commits — those
 * have their own ordering in wordWriter and are not in scope here.
 */
let tail: Promise<unknown> = Promise.resolve();

export function queueWordRead<T>(_label: string, fn: () => Promise<T>): Promise<T> {
  const next = tail.then(fn, fn);
  tail = next.catch(() => undefined);
  return next;
}

/**
 * Wrap an async task so overlapping calls share the run already in flight
 * instead of starting (and queueing) another. For periodic / event-driven
 * refreshes — e.g. the cursor-section poll — whose latest answer is all that
 * matters: without it, a probe slower than its trigger interval piles up in
 * ``queueWordRead`` without bound, and every read behind it (a chip click,
 * the empty-doc check) waits longer the longer the session runs.
 */
export function coalesceInFlight<T>(fn: () => Promise<T>): () => Promise<T> {
  let inFlight: Promise<T> | null = null;
  return () => {
    if (inFlight) return inFlight;
    const run = fn().finally(() => {
      inFlight = null;
    });
    inFlight = run;
    return run;
  };
}
